import React from 'react';
import styled from 'styled-components';
import { Body7, Container } from '@ui';
import { ChipRow, FilterChip } from './FilterChip';
import { describeIndex, formatIndexName } from './format';
import { NO_INDEX, toggleMembership } from './rowFilters';

// Narrowing the loaded rows to a SET of indices rather than one of them.
//
// A block of its own beneath the controls bar, not a control inside it. Nine chips of uppercase
// nowrap label (see BaseButton) cannot sit level with a 220px text input and two 34px selects, and
// the bar's one full-width flex slot is already spoken for by the knowable-window panel. This is the
// shape ColumnChooser reached for, for the same reason and in the same words — a row of its own,
// wrapping into a block rather than shoving the label along in front of it.
//
// Pressed toggles rather than checkboxes: this bar has none, and SkeletonButton already paints
// aria-pressed, so the visible state and the announced one cannot drift apart.

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 8px;
`;

// The one chip that is not an index gets a gap before it, so the row reads as "these indices, then
// the other answer" rather than as a row of peers one of which is odd.
const Trailing = styled.div`
  margin-left: 8px;
`;

// Not folded into formatIndexName: that function formats what the WIRE sent, and NO_INDEX is
// something this page made up.
const chipLabel = (key) => (key === NO_INDEX ? 'No index' : formatIndexName(key));

export const IndexFilter = ({ options = [], selected = [], hasUnindexed = false, onChange }) => {
  // Offered only when the response carries the flags — the same rule the selects above it follow.
  // Gated on the real indices alone: a lone "No index" chip over rows that are all in none would be
  // a filter whose only setting selects everything.
  if (options.length === 0) return null;

  const isOn = (key) => selected.includes(key);
  const toggle = (key) => onChange(toggleMembership(selected, key));

  return (
    <Container top={8}>
      <Row>
        {/* Two siblings rather than nested text, as KnowableWindowFilter labels its own block. The
            Indices COLUMN carries this word too, on its sort button, but the chips are named per
            index so nothing here collides with it by accessible name. */}
        <Body7 isLowContrast>Indices</Body7>
        {/* The accessible name the select carried, kept verbatim — it is what the table's tests
            query this control's presence and absence by. */}
        <ChipRow role="group" aria-label="Filter by index membership">
          {options.map((key) => (
            <FilterChip
              key={key}
              pressed={isOn(key)}
              label={`${chipLabel(key)} index`}
              title={describeIndex(key)}
              onPress={() => toggle(key)}
            >
              {chipLabel(key)}
            </FilterChip>
          ))}
          {/* The rows every other chip hides. Its accessible name drops the word the others append —
              "No index index" is not a name — so it is spelled out rather than built from
              chipLabel. */}
          {hasUnindexed && (
            <Trailing>
              <FilterChip
                pressed={isOn(NO_INDEX)}
                label="Companies in no index"
                title="Companies this read asked about that belong to none of the indices above"
                onPress={() => toggle(NO_INDEX)}
              >
                No index
              </FilterChip>
            </Trailing>
          )}
        </ChipRow>
      </Row>
    </Container>
  );
};
